/**
 * Delivery batching for Unity's sceneReady burst.
 *
 * Opening a project re-runs the whole handshake as ONE sceneReady message per scene, pushed
 * back-to-back in a single Unity frame (TimelineWebViewPanel.SendInitialSceneReadyMessages — a bare
 * loop of EvaluateJavaScript calls with nothing between them). Each one used to be its own
 * markSceneReady → its own `project` reference → its own board re-render; on a 317-scene project
 * that cadence, not the store math, is the multi-second frozen board after every reload. So the
 * handler buffers here instead, and the batch lands as one store write.
 *
 * SCOPE — this coalesces the OPEN/RELOAD rebroadcast, which is the measured wall. It does NOT
 * coalesce the board-fill drain: that route interleaves a `boardFillState` after every scene
 * (TimelineWebViewPanel's fill drain sends SendSceneReady then SendBoardFillStateSnapshot per
 * scene), and boardFillState is a foreign verb, so the dispatch seam flushes each scene
 * individually — N scenes still cost N writes there, exactly as before. That is deliberate: the
 * fill wall has not been measured, and exempting boardFillState would turn the seam from a total
 * rule into an enumerated exception. Revisit only if a measurement demands it.
 *
 * This is delivery batching and nothing else — no business logic, no filtering. The store still
 * decides what a push means (markScenesReady owns the merge, the pendingTileUpdates stash for rows
 * it doesn't have yet, and the skip-if-identical guard).
 *
 * ORDERING is the correctness core. A buffered push must never apply AFTER a write that happened
 * later, from either direction:
 *   - Inbound (Unity): every OTHER verb drains the buffer before its handler runs — one seam, in
 *     dispatchMessage (bridge/dispatch.ts), rather than a flush call per mutating handler that a
 *     future verb could forget to join.
 *   - Outbound (the app's own tile writers): acceptEnhancement, revertShotToLive,
 *     applyBatchGenerations, attachDroppedImage, setVideoKeyframe, loadBridgeHistoryAssets' apply
 *     pass (actions.ts) and attachVideo/removeVideo (projectStore.ts) each flush at their head,
 *     so a push buffered before a user's edit can't overwrite it up to 50 ms later.
 *
 *     INVARIANT for those writers: read the store state AFTER the flush, never before. A snapshot
 *     taken first predates the batch, so writing a project derived from it reverts every shot the
 *     flush just applied — and the batch is already consumed, so nothing re-applies it until
 *     Unity's next rebroadcast. Flushing and then writing stale state is worse than not flushing
 *     at all: it pays for a full board re-render and then discards the result.
 *   - loadProject drains it too, for the reloads that don't come in through a bridge verb.
 *
 * DEPENDENCY SHAPE: this module imports nothing but a type and the leaf merge rule
 * (tileVideoMerge.ts, which imports nothing) — it is a leaf, like its sibling
 * pendingTileUpdates.ts. projectStore installs the applier at module eval (setSceneReadyApplier).
 * That inversion is what lets projectStore's own tile writers flush at all: importing
 * `useProjectStore` here would make projectStore ↔ buffer a static cycle, and the house workaround
 * for that in projectStore — reaching bridge collaborators through a dynamic `import()` — cannot
 * serve a flush that must complete BEFORE the write it protects.
 */

import type { SceneReadyShotUpdate } from './projectStore.ts';
import { mergeTileVideo } from './tileVideoMerge.ts';

/** One scene's worth of buffered pushes, as markScenesReady consumes it. */
export interface SceneReadyBatchEntry {
  sceneId: number;
  shots: SceneReadyShotUpdate[];
}

/**
 * Safety-net delay for a push that no explicit flush ever follows: a lone mid-session targeted
 * tile push, or a burst whose allScenesReady is lost. Armed once when the buffer goes empty →
 * non-empty and deliberately NOT re-armed per message, so the window starts at the FIRST buffered
 * message and is never extended — a burst costs at most one extra flush per window instead of one
 * per message, and worst-case latency stays bounded at 50 ms rather than 50 ms after an
 * arbitrarily long burst. Long enough that the open handshake — N sceneReady plus allScenesReady,
 * all pushed in one Unity frame — is normally already drained by the allScenesReady flush before
 * it fires; short enough that a single tile still paints promptly.
 */
const FALLBACK_FLUSH_MS = 50;

/** sceneId → shotId → pending update, both in arrival order. */
const buffered = new Map<number, Map<number, SceneReadyShotUpdate>>();
let fallbackTimer: ReturnType<typeof setTimeout> | null = null;

type SceneReadyApplier = (entries: SceneReadyBatchEntry[]) => void;
let applyBatch: SceneReadyApplier | null = null;

/** Installed once by projectStore at module eval — see DEPENDENCY SHAPE above. Every producer of a
 *  buffered push imports projectStore statically (bridge/features/timeline.ts), so the applier is
 *  always in place before anything can buffer. */
export function setSceneReadyApplier(fn: SceneReadyApplier): void {
  applyBatch = fn;
}

/** Field-by-field `fresh ?? prev`, mirroring the store's merge: a thumbnail-only push must not
 *  erase a video URL an earlier push in the same batch brought. The video fields follow
 *  tileVideoMerge's rules, so a later `videoCleared` in the batch still takes that URL away. */
function merge(prev: SceneReadyShotUpdate, fresh: SceneReadyShotUpdate): SceneReadyShotUpdate {
  return {
    id: fresh.id,
    thumbnailDataUrl: fresh.thumbnailDataUrl ?? prev.thumbnailDataUrl,
    thumbHash: fresh.thumbHash ?? prev.thumbHash,
    ...mergeTileVideo(prev, fresh),
  };
}

/** Hold one sceneReady payload for the next flush. */
export function bufferSceneReady(sceneId: number, shots: SceneReadyShotUpdate[]): void {
  let scene = buffered.get(sceneId);
  if (!scene) {
    scene = new Map<number, SceneReadyShotUpdate>();
    buffered.set(sceneId, scene);
  }
  for (const fresh of shots) {
    const prev = scene.get(fresh.id);
    scene.set(fresh.id, prev ? merge(prev, fresh) : fresh);
  }
  if (fallbackTimer === null) {
    fallbackTimer = setTimeout(() => {
      fallbackTimer = null;
      flushSceneReadyBuffer();
    }, FALLBACK_FLUSH_MS);
  }
}

/**
 * Drain into the store as ONE write. Cheap no-op on an empty buffer, so callers flush
 * unconditionally rather than testing first. Synchronous end to end — assets.ts's manifest diff
 * reads the store, and must never observe a half-drained buffer.
 */
export function flushSceneReadyBuffer(): void {
  if (fallbackTimer !== null) {
    clearTimeout(fallbackTimer);
    fallbackTimer = null;
  }
  if (buffered.size === 0) return;
  if (!applyBatch) {
    // Unreachable by construction (see setSceneReadyApplier). Loud rather than silent, and the
    // buffer is deliberately NOT cleared — the next bufferSceneReady re-arms and retries, so a
    // broken install costs a delay rather than a board of blank tiles.
    console.error('[sceneReadyBuffer] flush with no applier installed — batch held, not applied');
    return;
  }
  const entries = [...buffered].map(([sceneId, shots]) => ({ sceneId, shots: [...shots.values()] }));
  buffered.clear();
  applyBatch(entries);
}

/** Test-only: drop the buffer and disarm the fallback so suites are order-independent. */
export function __resetSceneReadyBufferForTests(): void {
  if (fallbackTimer !== null) {
    clearTimeout(fallbackTimer);
    fallbackTimer = null;
  }
  buffered.clear();
}
